// panelAssign — deciding where a person can go, and refusing to act on a board
// that has moved underneath you.
//
// Pure. No React, no Firestore. The clock and the live data always arrive as
// arguments.
//
// This module exists because the control panel places people fast, mid-day,
// sometimes from a wall. Two things follow from that:
//
//   1. The destination list must distinguish an EMPTY room from an OCCUPIED
//      one, because those are different actions with different consequences.
//      Dropping someone into an empty room is additive and correctable by doing
//      it again. Dropping them into an occupied room DISPLACES a named person
//      who is physically in an operating room right now.
//
//   2. Two people can act at once. Assignment rows carry no version and no
//      audit trail, so last-write-wins silently: two AICs at two workstations
//      can place the same bench CRNA into two different rooms, both succeed,
//      and the first believes their room is staffed. `assignmentChangedSince`
//      is the cheapest honest defence available without a schema change —
//      re-read the row at commit time and refuse if the seat changed.

// ── Destinations ─────────────────────────────────────────────────────────────

// Every place a given person could be sent, grouped for the sheet.
//
//   { open: Lane[], occupied: Lane[] }
//
// `open` rooms take the write immediately; `occupied` rooms must answer "where
// does the displaced person go?" BEFORE anything is written. The grouping is
// the interaction: it is what stops a takeover looking like a placement.
//
// A lane the person is ALREADY in is excluded from both — "assign them to the
// room they are in" is not an action, and offering it invites a no-op write
// that still costs a stale-write check and an audit entry.
export function assignDestinations(lanes, personId) {
  const list = Array.isArray(lanes) ? lanes : [];
  const open = [];
  const occupied = [];

  for (const lane of list) {
    if (!lane || lane.roomId == null) continue;
    const holder = currentCrnaId(lane);
    if (personId && holder && holder === personId) continue;   // already there
    const target = holder ? occupied : open;
    target.push({
      roomId: lane.roomId,
      roomName: lane.roomName,
      sectionLabel: lane.sectionLabel,
      holderId: holder,
      holderName: currentCrnaName(lane),
    });
  }
  return { open, occupied };
}

// Who is in this lane's seat right now, per the assignment rows the model built.
//
// Reads the LAST case in the lane rather than the first: a room whose morning
// case has finished is staffed by whoever is on the afternoon one, and placing
// against a stale morning row would displace the wrong person.
export function currentCrnaId(lane) {
  const cases = (lane && Array.isArray(lane.cases)) ? lane.cases : [];
  for (let i = cases.length - 1; i >= 0; i -= 1) {
    const c = cases[i];
    if (c && c.ahpId) return c.ahpId;
  }
  return null;
}

export function currentCrnaName(lane) {
  const cases = (lane && Array.isArray(lane.cases)) ? lane.cases : [];
  for (let i = cases.length - 1; i >= 0; i -= 1) {
    const c = cases[i];
    if (c && c.ahpId) return c.ahpName || c.label || "";
  }
  return "";
}

// The assignment row a placement should be written to: the LAST case in the
// lane, for the same reason as above. Null when the room has no case at all —
// the panel cannot invent a case row, and says so rather than creating one.
export function targetCaseFor(lane) {
  const cases = (lane && Array.isArray(lane.cases)) ? lane.cases : [];
  return cases.length ? cases[cases.length - 1] : null;
}

// ── Where a displaced person goes ────────────────────────────────────────────

// The three honest answers, and there is no fourth.
//
// "Leave them nowhere" is deliberately NOT an option. A displaced provider who
// is off a room, not on the bench, and not marked departed is invisible: nobody
// calls them and they go home on their own. The existing bench flow performs the
// takeover first and asks afterwards, so closing the modal at that moment
// strands them. Here the answer is required before any write happens.
export const DISPLACED_OPTIONS = [
  { key: "bench", label: "Back to the bench", detail: "Available to place again." },
  { key: "home", label: "Home", detail: "Marked departed for the day." },
  { key: "transfer", label: "Transfer to another facility", detail: "Travels; the far end confirms arrival." },
];

export function isValidDisplacedChoice(key, { travelDest } = {}) {
  if (!DISPLACED_OPTIONS.some((o) => o.key === key)) return false;
  if (key === "transfer" && !travelDest) return false;
  return true;
}

// ── Stale-write protection ───────────────────────────────────────────────────

// Has the seat we are about to write changed since the sheet was opened?
//
// Returns null when it is safe to proceed, or a human sentence naming what
// changed when it is not.
//
// ⚠️ REFUSE, do not warn-and-proceed. A warn-and-proceed dialog is a confirm,
// and a confirm on a frequent action is the thing that trains an operator to
// click through every confirm on the screen — including the one that mattered.
// Refusing costs one re-open; proceeding costs a room that somebody believes is
// staffed and is not.
export function assignmentChangedSince(snapshotCase, liveCase) {
  if (!snapshotCase) return null;
  if (!liveCase) return "That case row is no longer on the board — reopen the panel.";

  const wasId = snapshotCase.ahpId || null;
  const nowId = liveCase.ahpId || null;
  if (wasId !== nowId) {
    return nowId
      ? `${liveCase.ahpName || "Someone else"} was just placed there — reopen to see the current board.`
      : "That room was just cleared by someone else — reopen to see the current board.";
  }

  const wasMd = snapshotCase.attendingId || null;
  const nowMd = liveCase.attendingId || null;
  if (wasMd !== nowMd) {
    return "The supervising doctor for that room just changed — reopen to see the current board.";
  }
  return null;
}

// May the panel write at all right now?
//
// A board that cannot prove it is live must not be acted upon: every action
// button is dead while the NOT LIVE band is showing. Acting on hours-old
// staffing is worse than not acting, because the operator believes they have
// fixed something.
// ⚠️ `ephemeral` — the board is running on a LOCAL, throwaway data plane
// (testing mode, or the sandbox). Both gates below exist to protect LIVE data,
// and on a plane that is destroyed when the session ends there is none to
// protect. Waiving them is what makes testing mode usable at all: a testing
// session is ALWAYS previewing somebody — that is the feature — so the preview
// gate refused every guarded gesture on the timeline while the banner above it
// promised "full write freedom" and "permission rules do not apply". The
// advice it offered ("Exit preview to make changes") could not be taken
// without ending the very session the user had just started.
//
// ⚠️ ROLE IS NOT WAIVED, and must not be. The point of previewing a person is
// to see what THAT person can do; D3 asks for "full write freedom per the
// previewed user's permissions", not for none at all.
//
// The caller passes the fact rather than this module reading a boot flag,
// because everything here is pure and testable — the same division
// `assignChoices` makes with `identified`.
export function writesAllowed({ canAct, connectionBand, previewing, ephemeral = false } = {}) {
  if (!canAct) return { allowed: false, reason: "role" };
  if (ephemeral) return { allowed: true, reason: null };
  if (connectionBand) return { allowed: false, reason: "not-live" };
  if (previewing) return { allowed: false, reason: "previewing" };
  return { allowed: true, reason: null };
}

export const WRITE_BLOCKED_COPY = {
  role: "",   // absent, not disabled — nothing is rendered at all
  "not-live": "This board is not live. Reconnect before changing anything.",
  previewing: "You are previewing another user. Exit preview to make changes.",
};

// ── Staged relief ────────────────────────────────────────────────────────────

// A relief staged more than this long ago has probably been forgotten. It is
// reported in the closing checklist, NEVER promoted to an alarm: the attention
// ladder on this panel is closed, and a new element may not outrank its place
// in it however useful it feels.
export const RELIEF_STALE_MIN = 30;

// Every assignment row carrying a staged (agreed but unconfirmed) handoff.
//
//   [{ roomId, roomName, caseId, assignment, outgoingName, incomingName,
//      seat: "crna" | "md" | "both", stale, staleReason }]
//
// Relief is a HANDOFF, not a break: it is agreed at 13:00 and happens at 15:00,
// so the pending state is the whole point and must stay visible while it lasts.
//
// Derived from the RAW assignment rows rather than from `buildTimeline`'s
// model, because the model does not carry the relief fields — and adding them
// would mean editing a module this feature is deliberately consuming unchanged.
// `resolveRoom(orRoom)` joins a row back to its room; callers pass the board's
// own resolver so the panel and the board never disagree about which room a row
// belongs to.
export function stagedReliefRows(assignments, {
  nowMin,
  resolveRoom,
  // `(staffId) => boolean` — has this person gone home? Passed in for the same
  // reason `resolveRoom` is: this module stays pure, and the caller already
  // holds `roster.departed`. Defaults to "nobody has left", so a caller that
  // does not pass it behaves exactly as before.
  isDeparted,
  staleAfterMin = RELIEF_STALE_MIN,
} = {}) {
  const rows = Array.isArray(assignments) ? assignments : [];
  const out = [];

  for (const a of rows) {
    if (!a) continue;
    const crna = a.crnaReliefId ? (a.crnaReliefName || "Relief") : null;
    const md = a.mdReliefId ? (a.mdReliefName || "Relief") : null;
    if (!crna && !md) continue;

    const room = typeof resolveRoom === "function" ? resolveRoom(a.orRoom) : null;

    const gone = typeof isDeparted === "function" ? isDeparted : () => false;
    const seat = crna && md ? "both" : crna ? "crna" : "md";

    // Stale on the clock is only half of it: a handoff whose outgoing person
    // has already left is late regardless of when it was staged, because the
    // room is now covered by somebody the board does not think is there.
    //
    // ⚠️ `ahpDepartTime` IS THE CRNA'S CLOCK, AND IT USED TO BE THE ONLY TEST.
    // On an MD handover it therefore asked when the room's *nurse anaesthetist*
    // was leaving in order to decide whether the *attending* had gone — so a
    // handover between two doctors read as calm and on-plan however late it
    // was. There is no `attendingDepartTime` on an assignment row at all; a
    // doctor's departure lives on the roster, which is why the caller has to
    // hand it in.
    const crnaDepartMin = hmToMinutesSafe(a.ahpDepartTime);
    const crnaClockPassed =
      crnaDepartMin != null && Number.isFinite(nowMin) && nowMin >= crnaDepartMin;
    const outgoingGone =
      seat === "md"
        ? gone(a.attendingId)
        : (crnaClockPassed || gone(a.ahpId) || (seat === "both" && gone(a.attendingId)));

    // ⚠️ AND NOBODY EVER ASKED ABOUT THE PERSON ARRIVING. A staged handover
    // whose INCOMING provider has gone home is not a plan — it is a room with
    // nobody coming, presented as one that is taken care of. That is worse than
    // a late handover, because a late one still has somebody to chase.
    const incomingGone = gone(a.crnaReliefId) || gone(a.mdReliefId);

    const stagedMin = Number.isFinite(a.reliefStagedMin) ? a.reliefStagedMin : null;
    const aged = stagedMin != null && Number.isFinite(nowMin) && nowMin - stagedMin >= staleAfterMin;

    out.push({
      roomId: room?.id || null,
      roomName: room?.name || a.orRoom || "",
      caseId: a.id,
      assignment: a,
      seat,
      outgoingName: crna ? (a.ahpName || "") : (a.attendingName || ""),
      incomingName: crna || md,
      stale: Boolean(aged || outgoingGone || incomingGone),
      // Worst-first: a relief nobody is coming to outranks a late one, which
      // outranks one that has merely sat a while.
      staleReason: incomingGone ? "incoming-left" : outgoingGone ? "departed" : aged ? "aged" : null,
      // The confirm control refuses on this. Confirming a takeover writes the
      // incoming person in as the room's provider for the rest of the day; doing
      // that for somebody who has gone home hands the room to nobody, in a way
      // the board then reports as settled.
      confirmable: !incomingGone,
    });
  }
  return out;
}

// Local, tiny, and deliberately not imported from timeline.js: this module is
// otherwise dependency-free, and one four-line parse is cheaper than a coupling.
function hmToMinutesSafe(hm) {
  if (typeof hm !== "string") return null;
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
